/**
 * CompoundEntryForm - CRUD for daily staking compound entries with running total
 */

import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { Badge } from '../../ui/components/Badge';
import { FeatherPlus, FeatherTrash2, FeatherEdit2 } from '@subframe/core';
import {
  useCompoundEntries,
  useCreateCompoundEntry,
  useUpdateCompoundEntry,
  useDeleteCompoundEntry,
} from '../../hooks/useDistribution';

interface CompoundEntryFormProps {
  projectSlug: string;
}

export const CompoundEntryForm: React.FC<CompoundEntryFormProps> = ({ projectSlug }) => {
  const { data, isLoading } = useCompoundEntries(projectSlug);
  const createMutation = useCreateCompoundEntry(projectSlug);
  const updateMutation = useUpdateCompoundEntry(projectSlug);
  const deleteMutation = useDeleteCompoundEntry(projectSlug);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const entries = data?.entries || [];
  const runningTotal = data?.running_total ?? 0;

  const resetForm = () => {
    setEntryDate(new Date().toISOString().slice(0, 10));
    setAmount('');
    setNotes('');
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount) return;

    if (editingId) {
      updateMutation.mutate(
        { id: editingId, entry_date: entryDate, amount: parseFloat(amount), notes: notes || undefined },
        { onSuccess: resetForm }
      );
    } else {
      createMutation.mutate(
        { entry_date: entryDate, amount: parseFloat(amount), notes: notes || undefined },
        { onSuccess: resetForm }
      );
    }
  };

  const handleEdit = (entry: typeof entries[0]) => {
    setEditingId(entry.id);
    setEntryDate(entry.entry_date.slice(0, 10));
    setAmount(String(entry.amount));
    setNotes(entry.notes || '');
    setShowForm(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-body-bold font-body-bold text-default-font">Compound Entries</span>
          <Badge variant="neutral">
            Running total: {Number(runningTotal).toLocaleString(undefined, { maximumFractionDigits: 6 })}
          </Badge>
        </div>
        {!showForm && (
          <Button
            variant="brand-secondary"
            size="small"
            icon={<FeatherPlus />}
            onClick={() => setShowForm(true)}
          >
            Add Entry
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 rounded border border-solid border-neutral-border p-4">
          <div className="flex gap-3">
            <TextField className="h-auto flex-1" label="Date">
              <TextField.Input
                type="date"
                value={entryDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEntryDate(e.target.value)}
              />
            </TextField>
            <TextField className="h-auto flex-1" label="Amount">
              <TextField.Input
                type="number"
                step="any"
                min="0"
                placeholder="e.g., 150.5"
                value={amount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
              />
            </TextField>
          </div>
          <TextField className="h-auto w-full" label="Notes (optional)">
            <TextField.Input
              value={notes}
              placeholder="e.g., Daily staking compound"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)}
            />
          </TextField>
          {(createMutation.error || updateMutation.error) && (
            <span className="text-caption font-caption text-red-600">
              {(createMutation.error || updateMutation.error)?.message}
            </span>
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="brand-primary" size="small" disabled={createMutation.isPending || updateMutation.isPending}>
              {editingId ? 'Update' : 'Add'}
            </Button>
            <Button variant="neutral-secondary" size="small" onClick={resetForm}>Cancel</Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-subtext-color py-4">Loading entries...</div>
      ) : entries.length === 0 ? (
        <div className="text-subtext-color py-4">No compound entries yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-body font-body">
            <thead>
              <tr className="border-b border-neutral-border text-left text-caption font-caption text-subtext-color">
                <th className="px-2 py-1">Date</th>
                <th className="px-2 py-1 text-right">Amount</th>
                <th className="px-2 py-1">Notes</th>
                <th className="px-2 py-1">Period</th>
                <th className="px-2 py-1 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className="border-b border-neutral-border">
                  <td className="px-2 py-1 text-default-font">{new Date(entry.entry_date).toLocaleDateString()}</td>
                  <td className="px-2 py-1 text-right text-default-font">
                    {Number(entry.amount).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                  </td>
                  <td className="px-2 py-1 text-subtext-color">{entry.notes || '-'}</td>
                  <td className="px-2 py-1">
                    {entry.period_id ? (
                      <Badge variant="success">Assigned</Badge>
                    ) : (
                      <Badge variant="warning">Unassigned</Badge>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    {!entry.period_id && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEdit(entry)}
                          className="p-1 rounded hover:bg-neutral-100"
                        >
                          <FeatherEdit2 className="w-3.5 h-3.5 text-subtext-color" />
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(entry.id)}
                          className="p-1 rounded hover:bg-red-50"
                        >
                          <FeatherTrash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
